import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { almacen } from '../api';
import { CABECERA_DEL_LIBRO, condicionDeVenta, filasDelLibro } from '../botica';
import { Boton, Cabecera, Cargando, Fallo, Pastilla, Rotulo, Vacio } from '../componentes';
import { descargarCsv } from '../csv';
import { cantidad, dia, diaDeLima, hora } from '../formato';

/**
 * El libro de recetas: cada línea vendida que pedía receta, con su lote.
 *
 * Es lo que se le enseña a quien inspecciona la botica: qué se dispensó, de
 * qué lote salió y con qué receta —el número y el médico de la retenida—. La
 * caja lo anota al cobrar (módulo `prescriptions`, migración 239); aquí solo
 * se lee y se baja. Ver docs/15 §2.40.
 */
export function LibroDeRecetas() {
  // El mes en curso: es como se suele pedir el libro.
  const [desde, setDesde] = useState(() => `${diaDeLima().slice(0, 7)}-01`);
  const [hasta, setHasta] = useState(() => diaDeLima());

  const consulta = useQuery({
    queryKey: ['libro-de-recetas', desde, hasta],
    queryFn: () => almacen.prescriptionBook(desde, hasta),
    enabled: desde !== '' && hasta !== '' && desde <= hasta,
  });

  const lineas = consulta.data ?? [];

  return (
    <>
      <Cabecera
        titulo="Libro de recetas"
        subtitulo="Lo vendido con receta, con su lote, para quien inspecciona"
        ayuda="Cada venta de algo que la ficha marca «Con receta» o «Receta retenida». La retenida guarda el número y el médico; las dos dicen de qué lote salió. Lo anulado después sigue aquí, marcado: la receta se vio igual."
        acciones={
          <>
            <div className="campo campo--en-linea">
              <Rotulo htmlFor="libro-desde" ayuda="El primer día, en hora de Lima.">Del</Rotulo>
              <input
                id="libro-desde"
                type="date"
                value={desde}
                max={hasta}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>
            <div className="campo campo--en-linea">
              <Rotulo htmlFor="libro-hasta" ayuda="El último día, incluido.">al</Rotulo>
              <input
                id="libro-hasta"
                type="date"
                value={hasta}
                min={desde}
                max={diaDeLima()}
                onChange={(e) => setHasta(e.target.value)}
              />
            </div>
            {/* Lo que pide el inspector se lleva en un archivo, no en una pantalla. */}
            <Boton
              onClick={() =>
                descargarCsv(
                  `libro-de-recetas-${desde}-a-${hasta}.csv`,
                  CABECERA_DEL_LIBRO,
                  filasDelLibro(lineas),
                )
              }
              disabled={lineas.length === 0}
            >
              Exportar
            </Boton>
          </>
        }
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        <div className="tabla-envoltura">
          {consulta.isLoading ? (
            <Cargando que="el libro de recetas" />
          ) : lineas.length === 0 ? (
            <Vacio
              titulo="Nada vendido con receta en esos días"
              mensaje="Cuando la caja cobre algo que pide receta, la venta queda aquí con su lote y, si es retenida, con el número y el médico."
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Producto</th>
                  <th className="numero">Cantidad</th>
                  <th>Lote</th>
                  <th>Condición</th>
                  <th>Receta</th>
                  <th>Venta</th>
                  <th>Atendió</th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((linea, indice) => (
                  <tr key={`${linea.recordedAt}-${indice}`}>
                    <td className="apagado mono">
                      {dia(linea.recordedAt)} {hora(linea.recordedAt)}
                    </td>
                    <td>
                      <strong>{linea.productName}</strong>
                      {linea.cancelled ? <Pastilla texto="anulada" tono="aviso" /> : null}
                      {linea.withoutPrescription === true ? (
                        <Pastilla texto="sin receta" tono="aviso" />
                      ) : null}
                    </td>
                    <td className="numero">{cantidad(linea.quantity)}</td>
                    <td className="mono">{linea.lots === '' ? 'Sin lote' : linea.lots}</td>
                    <td>
                      <Pastilla
                        texto={condicionDeVenta(linea.saleCondition)}
                        tono={linea.saleCondition === 'retained_prescription' ? 'marca' : 'neutro'}
                      />
                    </td>
                    <td>
                      <div className="celda-nombre__texto">
                        <strong>{linea.prescriptionNumber ?? '—'}</strong>
                        {linea.prescriber === null ? null : <span>{linea.prescriber}</span>}
                      </div>
                    </td>
                    <td className="apagado mono">
                      {linea.orderNumber === null ? '' : `#${linea.orderNumber}`}
                      {linea.documentNumber === null ? '' : ` · ${linea.documentNumber}`}
                    </td>
                    <td className="apagado">{linea.checkedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
